import { useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PLAN_BY_ID, endpointsBullet, type PlanId } from "@/lib/plan-catalog";
import { WHATSAPP_DISPLAY, WHATSAPP_HREF } from "@/lib/contact";

// -----------------------------------------------------------------------------
// Content — ported verbatim from docs/frontend/_extracted/variants/shared.jsx
// -----------------------------------------------------------------------------

const PM_CONTENT = {
  nav: ["Platform", "MSP", "Pricing", "Docs"] as const,
  hero: {
    sub: "prayingmantis watches your Microsoft 365, Google Workspace and endpoint activity, and only surfaces the signals that warrant your attention — with the investigation already started.",
  },
  stats: [
    { n: "55+", l: "detection rules", sub: "Sigma-based, tuned for SMB" },
    { n: "AI", l: "investigation", sub: "The story, not the log line" },
    { n: "Same-day", l: "deployment", sub: "Minutes to first alert per source" },
    { n: "Audit", l: "logs & reports", sub: "Compliance evidence on demand" },
  ],
  problems: [
    {
      n: "01",
      t: "Alert fatigue",
      b: "Security teams wade through hundreds of alerts a day. Most are false positives. The real threats sink into the noise.",
    },
    {
      n: "02",
      t: "Skills shortage",
      b: "Qualified SOC analysts cost $150K+ and take months to hire. Small teams cannot staff a 24/7 rotation. Attackers know.",
    },
    {
      n: "03",
      t: "Tool sprawl",
      b: "The average SMB runs 5+ disconnected security tools. No single pane of glass. Slower response. Missed correlations.",
    },
  ],
  features: [
    {
      k: "Detect",
      t: "Signal, not noise",
      b: "Curated Sigma detections and threat intel surface the few alerts worth acting on.",
    },
    {
      k: "Investigate",
      t: "The story, not the log line",
      b: "AI triage, Athena search, and entity timelines turn scattered logs into one attack story.",
    },
    {
      k: "MSP",
      t: "Every client, one pane",
      b: "Multi-tenant dashboards, white-label branding, batch onboarding, and PSA handoff.",
    },
    {
      k: "Integrations",
      t: "Your stack, today",
      b: "Microsoft 365 and Google Workspace via API polling; endpoints via a lightweight agent. Same-day setup.",
    },
  ],
  integrations: [
    { g: "Identity", items: ["Microsoft Entra ID"] },
    { g: "Workspace", items: ["Google Workspace"] },
    { g: "Endpoint", items: ["Linux · available now", "Windows · available now", "macOS · coming soon"] },
  ],
  msp: {
    b: "White-label prayingmantis on your own domain. Onboard dozens of tenants in a batch. Bill per-client. Keep your PSA in the loop.",
    bullets: [
      "Multi-tenant portal with per-client dashboards",
      "White-label branding on custom domains",
      "Batch onboarding — dozens of tenants at once",
      "PSA: ConnectWise, Autotask, HaloPSA",
      "Per-client billing and usage reporting",
      "Role-based access across your whole book",
    ],
  },
  // Plan facts (price, endpoint cap, retention) come from the shared catalog;
  // only the marketing copy around them lives here.
  pricing: [
    {
      plan: "starter",
      sub: "A 30-day trial to kick the tires.",
      lead: "",
      f: [
        endpointsBullet(PLAN_BY_ID.starter),
        "Core detection rules",
        "Email alerts",
        PLAN_BY_ID.starter.retention,
      ],
      cta: "Start free trial",
      note: "No credit card · subject to fair use.",
    },
    {
      plan: "essential",
      sub: "For lean teams getting started.",
      lead: "Everything in Starter, plus:",
      f: [
        endpointsBullet(PLAN_BY_ID.essential),
        "All detection rules",
        "Slack + webhook alerts",
        "AI investigation",
        PLAN_BY_ID.essential.retention,
      ],
      cta: "Start free trial",
      note: "30-day trial · no credit card · subject to fair use.",
    },
    {
      plan: "growth",
      sub: "Most SMBs land here.",
      lead: "Everything in Essential, plus:",
      f: [
        endpointsBullet(PLAN_BY_ID.growth),
        PLAN_BY_ID.growth.retention,
      ],
      cta: "Start free trial",
      note: "30-day trial · no credit card · subject to fair use.",
    },
    {
      plan: "business",
      sub: "Dedicated security teams.",
      lead: "Everything in Growth, plus:",
      f: [
        endpointsBullet(PLAN_BY_ID.business),
        "Custom rules",
        "Active response",
        PLAN_BY_ID.business.retention,
      ],
      cta: "Start free trial",
      note: "30-day trial · no credit card · subject to fair use.",
    },
    {
      plan: "enterprise",
      sub: "MSPs and large orgs.",
      lead: "Everything in Business, plus:",
      f: [
        endpointsBullet(PLAN_BY_ID.enterprise),
        "Multi-tenant MSP portal",
        "Custom branding",
        PLAN_BY_ID.enterprise.retention,
        "Dedicated support",
      ],
      cta: "Contact sales",
      note: "Tailored SLAs.",
    },
  ]
    .filter((tier) => !PLAN_BY_ID[tier.plan as PlanId].hidden)
    .map((tier) => ({
      ...tier,
      n: PLAN_BY_ID[tier.plan as PlanId].name,
      p: PLAN_BY_ID[tier.plan as PlanId].price,
      per: PLAN_BY_ID[tier.plan as PlanId].per,
      popular: PLAN_BY_ID[tier.plan as PlanId].popular ?? false,
    })),
  trust: [
    "OCSF-aligned data model",
    "AES-256 encryption at rest",
    "Full audit logs",
    "Role-based access control",
  ],
  cta: {
    contact: "sales@prayingmantis.cloud",
  },
} as const;

const INTEGRATION_MAP_AREAS = ["identity", "workspace", "endpoint"] as const;

// -----------------------------------------------------------------------------
// Palette — mirrors v2 tokens from the mockup. Applied via CSS custom properties
// scoped to .pm-landing so the rest of the app theme is untouched.
// -----------------------------------------------------------------------------

const palette = {
  bg: "#07090c",
  panel: "#0e1116",
  ink: "#f4f5f7",
  sub: "#8a93a2",
  subDim: "#5a6270",
  border: "#ffffff0e",
  borderStrong: "#ffffff1a",
  accent: "oklch(0.78 0.2 148)",
  accentDim: "oklch(0.78 0.2 148 / 0.18)",
  blue: "oklch(0.72 0.15 240)",
} as const;

// Map anchors for nav items → section IDs on the page.
const NAV_TARGETS: Record<(typeof PM_CONTENT.nav)[number], string> = {
  Platform: "#platform",
  MSP: "#msp",
  Pricing: "#pricing",
  Docs: "/docs",
};

// -----------------------------------------------------------------------------
// Primitives
// -----------------------------------------------------------------------------

type BtnVariant = "primary" | "ghost" | "default";

function Btn({
  children,
  variant = "default",
  small,
  style,
  to,
  href,
  onClick,
  ariaLabel,
  fullWidth,
}: {
  children: ReactNode;
  variant?: BtnVariant;
  small?: boolean;
  style?: CSSProperties;
  to?: string;
  href?: string;
  onClick?: () => void;
  ariaLabel?: string;
  fullWidth?: boolean;
}) {
  const base: CSSProperties = {
    fontFamily: "inherit",
    fontSize: small ? 13 : 14,
    fontWeight: 500,
    letterSpacing: "-0.005em",
    padding: small ? "8px 14px" : "12px 20px",
    borderRadius: 999,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: fullWidth ? "center" : undefined,
    gap: 8,
    width: fullWidth ? "100%" : undefined,
    border: "1px solid transparent",
    textDecoration: "none",
  };
  let applied: CSSProperties;
  if (variant === "primary") {
    applied = { ...base, background: palette.accent, color: "#07090c", fontWeight: 600 };
  } else if (variant === "ghost") {
    applied = { ...base, background: "transparent", color: palette.ink };
  } else {
    applied = {
      ...base,
      background: "#ffffff10",
      color: palette.ink,
      border: `1px solid ${palette.borderStrong}`,
    };
  }
  const merged = { ...applied, ...style };

  if (to) {
    return (
      <Link to={to} style={merged} aria-label={ariaLabel}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} style={merged} aria-label={ariaLabel}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} style={merged} aria-label={ariaLabel}>
      {children}
    </button>
  );
}

function Pill({ children, dot, tone }: { children: ReactNode; dot?: boolean; tone?: "danger" }) {
  const color = tone === "danger" ? "oklch(0.68 0.22 22)" : palette.accent;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "5px 11px",
        borderRadius: 999,
        background: "#ffffff08",
        border: `1px solid ${palette.borderStrong}`,
        fontSize: 12,
        color: palette.ink,
        fontWeight: 500,
      }}
    >
      {dot && (
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 12px ${color}`,
          }}
        />
      )}
      {children}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Landing page
// -----------------------------------------------------------------------------

export default function LandingPage() {
  const [featTab, setFeatTab] = useState(0);
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div
      className="pm-landing"
      style={{
        width: "100%",
        background: palette.bg,
        color: palette.ink,
        fontFamily: "'Inter Tight', system-ui, sans-serif",
        letterSpacing: "-0.01em",
        position: "relative",
        // "clip" (not "hidden") — overflow:hidden creates a scroll container,
        // which breaks position:sticky on the nav bar.
        overflow: "clip",
        minHeight: "100vh",
      }}
    >
      <style>{`
        .pm-landing { -webkit-font-smoothing: antialiased; }
        .pm-landing a { color: inherit; }
        .pm-landing .pm-link {
          color: ${palette.sub};
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: color .12s;
        }
        .pm-landing .pm-link:hover { color: ${palette.ink}; }
        .pm-landing .pm-card {
          background: ${palette.panel};
          border: 1px solid ${palette.border};
          border-radius: 20px;
        }
        .pm-landing .pm-integration-map {
          grid-template-columns: minmax(170px, 1fr) 220px minmax(170px, 1fr);
          grid-template-areas:
            "identity hub workspace"
            ". endpoint .";
        }
        .pm-landing .pm-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .pm-landing .pm-serif { font-family: 'Instrument Serif', Georgia, serif; }
        .pm-landing .pm-skip {
          position: absolute;
          left: -9999px;
          top: 0;
          z-index: 20;
          background: ${palette.accent};
          color: #07090c;
          padding: 8px 14px;
          border-radius: 0 0 8px 0;
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
        }
        .pm-landing .pm-skip:focus { left: 0; }
        .pm-landing .pm-nav-burger {
          display: none;
          background: none;
          border: none;
          color: ${palette.ink};
          font-size: 22px;
          line-height: 1;
          cursor: pointer;
          padding: 6px 10px;
        }
        @keyframes pm-tick { 0%,100%{opacity:1} 50%{opacity:.5} }
        @media (max-width: 960px) {
          .pm-landing .pm-hero-grid,
          .pm-landing .pm-msp-grid,
          .pm-landing .pm-features-grid { grid-template-columns: 1fr !important; }
          .pm-landing .pm-feature-tiles { grid-template-columns: 1fr !important; }
          .pm-landing .pm-integration-shell { grid-template-columns: 1fr !important; }
          .pm-landing .pm-integration-map { grid-template-columns: 1fr !important; grid-template-areas: none !important; }
          .pm-landing .pm-map-node, .pm-landing .pm-map-hub { grid-area: auto !important; }
          .pm-landing .pm-map-lines { display: none !important; }
          .pm-landing .pm-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .pm-landing .pm-integrations-grid,
          .pm-landing .pm-pricing-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .pm-landing .pm-problems-grid { grid-template-columns: 1fr !important; }
          .pm-landing .pm-hero-h1 { font-size: 64px !important; }
          .pm-landing .pm-section-h2 { font-size: 44px !important; }
        }
        @media (max-width: 960px) {
          .pm-landing .pm-stats-grid > div {
            border-right: none !important;
            border-bottom: 1px solid ${palette.border};
          }
          .pm-landing .pm-stats-grid > div:last-child { border-bottom: none; }
        }
        @media (max-width: 600px) {
          .pm-landing .pm-integrations-grid,
          .pm-landing .pm-pricing-grid,
          .pm-landing .pm-stats-grid { grid-template-columns: 1fr !important; }
          .pm-landing .pm-nav-links { display: none !important; }
          .pm-landing .pm-nav-burger { display: inline-flex !important; }
          .pm-landing .pm-hero-h1 { font-size: 48px !important; }
          .pm-landing .pm-section-h2 { font-size: 36px !important; }
        }
      `}</style>

      <a href="#pm-main" className="pm-skip">
        Skip to content
      </a>

      {/* NAV */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "rgba(7,9,12,0.7)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "16px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link
            to="/"
            style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}
          >
            <img src="/logo.png" alt="prayingmantis.cloud" style={{ height: 36, display: "block" }} />
          </Link>
          <nav
            className="pm-nav-links"
            aria-label="Main navigation"
            style={{ display: "flex", gap: 28 }}
          >
            {PM_CONTENT.nav.map((n) => {
              const target = NAV_TARGETS[n];
              if (target.startsWith("/")) {
                return (
                  <Link key={n} to={target} className="pm-link">
                    {n}
                  </Link>
                );
              }
              return (
                <a key={n} href={target} className="pm-link">
                  {n}
                </a>
              );
            })}
          </nav>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Link to="/login" className="pm-link">
              Sign in
            </Link>
            <Btn variant="primary" small to="/signup">
              Start free<span aria-hidden>→</span>
            </Btn>
            <button
              type="button"
              className="pm-nav-burger"
              aria-label={navOpen ? "Close menu" : "Open menu"}
              aria-expanded={navOpen}
              onClick={() => setNavOpen(!navOpen)}
            >
              {navOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>
        {navOpen && (
          <nav
            aria-label="Main navigation"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              padding: "4px 32px 16px",
              borderTop: `1px solid ${palette.border}`,
            }}
          >
            {PM_CONTENT.nav.map((n) => {
              const target = NAV_TARGETS[n];
              const close = () => setNavOpen(false);
              if (target.startsWith("/")) {
                return (
                  <Link key={n} to={target} className="pm-link" style={{ padding: "8px 0" }} onClick={close}>
                    {n}
                  </Link>
                );
              }
              return (
                <a key={n} href={target} className="pm-link" style={{ padding: "8px 0" }} onClick={close}>
                  {n}
                </a>
              );
            })}
          </nav>
        )}
      </div>

      {/* HERO */}
      <section id="pm-main" style={{ position: "relative", padding: "88px 32px 56px", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }} aria-hidden>
          <div
            style={{
              position: "absolute",
              top: -200,
              left: "10%",
              width: 600,
              height: 600,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${palette.accent}2e, transparent 60%)`,
              filter: "blur(40px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 100,
              right: "5%",
              width: 500,
              height: 500,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${palette.blue}1f, transparent 60%)`,
              filter: "blur(40px)",
            }}
          />
        </div>
        <div style={{ maxWidth: 1280, margin: "0 auto", position: "relative" }}>
          <div
            className="pm-hero-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 48,
              alignItems: "center",
            }}
          >
            <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto" }}>
              <h1
                className="pm-hero-h1"
                style={{
                  fontSize: 108,
                  fontWeight: 500,
                  letterSpacing: "-0.045em",
                  lineHeight: 0.94,
                  margin: "24px 0 0",
                  textWrap: "balance",
                }}
              >
                Security
                <br />
                that{" "}
                <span
                  className="pm-serif"
                  style={{ fontStyle: "italic", fontWeight: 400, color: palette.accent }}
                >
                  moves first.
                </span>
              </h1>
              <p
                style={{
                  fontSize: 20,
                  lineHeight: 1.45,
                  color: palette.sub,
                  margin: "28px auto 0",
                  maxWidth: 540,
                  textWrap: "pretty",
                }}
              >
                {PM_CONTENT.hero.sub}
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 32, flexWrap: "wrap", justifyContent: "center" }}>
                <Btn variant="primary" to="/signup">
                  Start free trial<span aria-hidden>→</span>
                </Btn>
                <Btn to="/docs">Read the docs</Btn>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 20,
                  marginTop: 18,
                  fontSize: 13,
                  color: palette.subDim,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <span>No credit card</span>
                <span aria-hidden>·</span>
                <span>Same-day deployment</span>
                <span aria-hidden>·</span>
                <span>Cancel anytime</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section style={{ maxWidth: 1280, margin: "0 auto", padding: "40px 32px" }}>
        <div
          className="pm-card pm-stats-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}
        >
          {PM_CONTENT.stats.map((s, i) => (
            <div
              key={s.l}
              style={{
                padding: "28px 28px",
                borderRight: i < PM_CONTENT.stats.length - 1 ? `1px solid ${palette.border}` : "none",
              }}
            >
              <div
                style={{
                  fontSize: 52,
                  fontWeight: 500,
                  letterSpacing: "-0.04em",
                  lineHeight: 1,
                }}
              >
                {s.n}
              </div>
              <div style={{ fontSize: 13, color: palette.ink, marginTop: 10, fontWeight: 500 }}>
                {s.l}
              </div>
              <div style={{ fontSize: 12.5, color: palette.sub, marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM */}
      <section style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 32px" }}>
        <div style={{ maxWidth: 760 }}>
          <Pill>The problem</Pill>
          <h2
            className="pm-section-h2"
            style={{
              fontSize: 60,
              fontWeight: 500,
              letterSpacing: "-0.035em",
              margin: "20px 0 0",
              lineHeight: 1.02,
              textWrap: "balance",
            }}
          >
            The old SOC
            <br />
            <span
              className="pm-serif"
              style={{ fontStyle: "italic", fontWeight: 400, color: palette.sub }}
            >
              can&apos;t keep up.
            </span>
          </h2>
        </div>
        <div
          className="pm-problems-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
            marginTop: 40,
          }}
        >
          {PM_CONTENT.problems.map((p) => (
            <div key={p.n} className="pm-card" style={{ padding: 28, minHeight: 220 }}>
              <div
                className="pm-mono"
                style={{
                  fontSize: 12,
                  color: palette.accent,
                  letterSpacing: "0.14em",
                }}
              >
                {p.n}
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 500,
                  marginTop: 16,
                  letterSpacing: "-0.02em",
                }}
              >
                {p.t}
              </div>
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.55,
                  color: palette.sub,
                  marginTop: 10,
                  textWrap: "pretty",
                }}
              >
                {p.b}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section
        id="platform"
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "40px 32px 64px",
          scrollMarginTop: 80,
        }}
      >
        <div style={{ marginBottom: 28 }}>
          <Pill>The platform</Pill>
          <h2
            className="pm-section-h2"
            style={{
              fontSize: 56,
              fontWeight: 500,
              letterSpacing: "-0.035em",
              margin: "18px 0 0",
              lineHeight: 1,
              textWrap: "balance",
            }}
          >
            Detect. Investigate.{" "}
            <span
              className="pm-serif"
              style={{ fontStyle: "italic", fontWeight: 400, color: palette.accent }}
            >
              Respond.
            </span>
          </h2>
          <p style={{ fontSize: 16, color: palette.sub, maxWidth: 560, marginTop: 12 }}>
            One sharp workflow from alert to containment.
          </p>
        </div>
        <div className="pm-card" style={{ overflow: "hidden", padding: 0 }}>
          <div
            aria-label="Platform capabilities"
            style={{
              display: "flex",
              gap: 8,
              padding: 12,
              borderBottom: `1px solid ${palette.border}`,
              flexWrap: "wrap",
            }}
          >
            {PM_CONTENT.features.map((f, i) => {
              const active = i === featTab;
              return (
                <button
                  key={f.k}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFeatTab(i)}
                  style={{
                    border: active ? `1px solid ${palette.accent}55` : `1px solid ${palette.border}`,
                    borderRadius: 999,
                    background: active ? palette.accentDim : "#ffffff05",
                    color: active ? palette.accent : palette.sub,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "8px 13px",
                    transition: "all .15s",
                  }}
                >
                  {String(i + 1).padStart(2, "0")} · {f.k}
                </button>
              );
            })}
          </div>
          <div
            className="pm-features-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "0.95fr 1.05fr",
              gap: 28,
              padding: 30,
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                background: `radial-gradient(520px 220px at 78% 38%, ${palette.accent}16, transparent 65%)`,
              }}
            />
            <div style={{ position: "relative" }}>
              <div
                className="pm-mono"
                style={{
                  fontSize: 11,
                  color: palette.accent,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                {PM_CONTENT.features[featTab].k}
              </div>
              <div
                style={{
                  fontSize: 36,
                  fontWeight: 500,
                  letterSpacing: "-0.035em",
                  marginTop: 10,
                  lineHeight: 1.02,
                  textWrap: "balance",
                }}
              >
                {PM_CONTENT.features[featTab].t}
              </div>
              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.45,
                  color: palette.sub,
                  marginTop: 12,
                  maxWidth: 500,
                  textWrap: "pretty",
                }}
              >
                {PM_CONTENT.features[featTab].b}
              </p>
            </div>
            <div
              className="pm-feature-tiles"
              style={{
                position: "relative",
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 10,
              }}
            >
              {[
                ["01", "Ingest", "Cloud, SaaS, endpoint"],
                ["02", "Correlate", "Identity + host context"],
                ["03", "Act", "Signed playbooks"],
              ].map(([n, t, b]) => (
                <div
                  key={t}
                  style={{
                    border: `1px solid ${palette.border}`,
                    borderRadius: 16,
                    background: "#05070a66",
                    padding: 16,
                    minHeight: 116,
                  }}
                >
                  <div className="pm-mono" style={{ fontSize: 11, color: palette.accent }}>
                    {n}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, marginTop: 18 }}>{t}</div>
                  <div style={{ fontSize: 13, color: palette.sub, marginTop: 4 }}>{b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section style={{ maxWidth: 1280, margin: "0 auto", padding: "40px 32px 80px" }}>
        <div
          className="pm-card pm-integration-shell"
          style={{
            display: "grid",
            gridTemplateColumns: "0.78fr 1.22fr",
            gap: 28,
            padding: 28,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(640px 320px at 65% 40%, ${palette.accent}12, transparent 65%)`,
            }}
          />
          <div style={{ position: "relative", alignSelf: "center" }}>
            <Pill>Integrations</Pill>
            <h2
              className="pm-section-h2"
              style={{
                fontSize: 44,
                fontWeight: 500,
                letterSpacing: "-0.035em",
                margin: "16px 0 0",
                lineHeight: 1,
                textWrap: "balance",
              }}
            >
              Connect your stack.{" "}
              <span className="pm-serif" style={{ fontStyle: "italic", fontWeight: 400 }}>
                See the graph.
              </span>
            </h2>
            <p style={{ fontSize: 16, color: palette.sub, lineHeight: 1.45, marginTop: 14 }}>
              Microsoft, Google, Linux, and Windows endpoints normalize into one investigation map —
              no rip-and-replace. macOS endpoint support is coming soon.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 20 }}>
              {["API polling", "Endpoint agent", "Microsoft 365", "Same-day setup"].map((item) => (
                <span
                  key={item}
                  style={{
                    border: `1px solid ${palette.border}`,
                    borderRadius: 999,
                    color: palette.sub,
                    fontSize: 12.5,
                    padding: "6px 10px",
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
          <div
            className="pm-integration-map"
            style={{
              position: "relative",
              display: "grid",
              gap: 12,
              alignItems: "center",
              minHeight: 330,
            }}
          >
            <svg
              className="pm-map-lines"
              aria-hidden
              viewBox="0 0 640 330"
              preserveAspectRatio="none"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            >
              <g stroke={palette.accent} strokeOpacity="0.28" strokeWidth="1.5" fill="none">
                <path d="M320 140 C245 86 190 64 110 62" />
                <path d="M320 140 C398 86 455 64 530 62" />
                <path d="M320 160 C320 214 320 246 320 278" />
              </g>
            </svg>
            <div
              className="pm-map-hub"
              style={{
                gridArea: "hub",
                border: `1px solid ${palette.accent}44`,
                background: `linear-gradient(180deg, ${palette.accentDim}, #05070a99)`,
                borderRadius: 22,
                padding: 22,
                textAlign: "center",
                boxShadow: `0 0 40px ${palette.accent}12`,
                zIndex: 1,
              }}
            >
              <div className="pm-mono" style={{ fontSize: 11, color: palette.accent }}>
                CORE GRAPH
              </div>
              <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.03em", marginTop: 8 }}>
                prayingmantis
              </div>
              <div style={{ fontSize: 13, color: palette.sub, marginTop: 6 }}>Alerts · entities · response</div>
            </div>
            {PM_CONTENT.integrations.map((g, i) => {
              const area = INTEGRATION_MAP_AREAS[i] ?? "auto";
              return (
                <div
                  key={g.g}
                  className="pm-map-node"
                  style={{
                    gridArea: area,
                    border: `1px solid ${palette.border}`,
                    borderRadius: 16,
                    background: "#05070ad9",
                    padding: 16,
                    zIndex: 1,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ fontSize: 15, fontWeight: 650 }}>{g.g}</div>
                    <div className="pm-mono" style={{ fontSize: 10.5, color: palette.accent }}>
                      0{i + 1}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                    {g.items.slice(0, 3).map((it) => (
                      <span
                        key={it}
                        style={{
                          background: "#ffffff08",
                          borderRadius: 999,
                          color: palette.sub,
                          fontSize: 12,
                          padding: "4px 8px",
                        }}
                      >
                        {it}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MSP */}
      <section id="msp" style={{ padding: "40px 32px 80px", scrollMarginTop: 80 }}>
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            borderRadius: 28,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${palette.accent} 0%, oklch(0.6 0.2 148) 100%)`,
            color: "#05070a",
            padding: "64px 56px",
            position: "relative",
          }}
        >
          <div
            className="pm-msp-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1.1fr 1fr",
              gap: 56,
            }}
          >
            <div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 11px",
                  borderRadius: 999,
                  background: "#05070a18",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                For MSPs
              </span>
              <h2
                className="pm-section-h2"
                style={{
                  fontSize: 60,
                  fontWeight: 500,
                  letterSpacing: "-0.035em",
                  margin: "20px 0 18px",
                  lineHeight: 1,
                  textWrap: "balance",
                }}
              >
                Every client.
                <br />
                <span className="pm-serif" style={{ fontStyle: "italic", fontWeight: 400 }}>
                  One portal.
                </span>
              </h2>
              <p
                style={{
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: "#05070acc",
                  maxWidth: 440,
                  textWrap: "pretty",
                }}
              >
                {PM_CONTENT.msp.b}
              </p>
              <div style={{ marginTop: 28 }}>
                <Btn
                  variant="primary"
                  to="/signup"
                  style={{ background: "#05070a", color: palette.accent }}
                >
                  Start MSP trial<span aria-hidden>→</span>
                </Btn>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {PM_CONTENT.msp.bullets.map((b) => (
                <div
                  key={b}
                  style={{
                    background: "#05070a12",
                    borderRadius: 14,
                    padding: "14px 14px",
                    fontSize: 14,
                    lineHeight: 1.35,
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 999,
                      background: "#05070a",
                      color: palette.accent,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      marginTop: 2,
                    }}
                  >
                    ✓
                  </span>
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section
        id="pricing"
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "40px 32px 80px",
          scrollMarginTop: 80,
        }}
      >
        <div style={{ marginBottom: 32 }}>
          <Pill>Pricing</Pill>
          <h2
            className="pm-section-h2"
            style={{
              fontSize: 60,
              fontWeight: 500,
              letterSpacing: "-0.035em",
              margin: "20px 0 0",
              lineHeight: 1.02,
              textWrap: "balance",
            }}
          >
            Enterprise security.{" "}
            <span
              className="pm-serif"
              style={{ fontStyle: "italic", fontWeight: 400, color: palette.accent }}
            >
              SMB pricing.
            </span>
          </h2>
          <p style={{ fontSize: 14, color: palette.sub, lineHeight: 1.5, marginTop: 14 }}>
            Endpoint allowances cover the Linux and Windows agents. macOS agents are coming soon
            and are not included in the currently available service.
          </p>
        </div>
        <div
          className="pm-pricing-grid"
          style={{ display: "grid", gridTemplateColumns: `repeat(${PM_CONTENT.pricing.length}, 1fr)`, gap: 12 }}
        >
          {PM_CONTENT.pricing.map((p) => (
            <div
              key={p.n}
              className="pm-card"
              style={{
                padding: 28,
                minHeight: 440,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                background: p.popular
                  ? `linear-gradient(180deg, ${palette.accentDim}, ${palette.panel})`
                  : palette.panel,
                border: p.popular
                  ? `1px solid ${palette.accent}55`
                  : `1px solid ${palette.border}`,
              }}
            >
              {p.popular && (
                <div
                  style={{
                    position: "absolute",
                    top: -11,
                    left: 28,
                    background: palette.accent,
                    color: "#05070a",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    padding: "4px 12px",
                    borderRadius: 999,
                  }}
                >
                  Most popular
                </div>
              )}
              <div style={{ fontSize: 14, fontWeight: 600 }}>{p.n}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 14 }}>
                <span
                  style={{
                    fontSize: 44,
                    fontWeight: 500,
                    letterSpacing: "-0.04em",
                    lineHeight: 1,
                  }}
                >
                  {p.p}
                </span>
                {p.per && <span style={{ fontSize: 13, color: palette.sub }}>{p.per}</span>}
              </div>
              <div style={{ fontSize: 13.5, color: palette.sub, marginTop: 6 }}>{p.sub}</div>
              <div style={{ height: 1, background: palette.border, margin: "18px 0" }} />
              <div style={{ flex: 1 }}>
                {p.lead && (
                  <div style={{ fontSize: 12.5, color: palette.subDim, paddingBottom: 6 }}>
                    {p.lead}
                  </div>
                )}
                {p.f.map((f) => (
                  <div
                    key={f}
                    style={{ fontSize: 14, padding: "6px 0", display: "flex", gap: 10 }}
                  >
                    <span style={{ color: palette.accent }} aria-hidden>
                      ✓
                    </span>{" "}
                    {f}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20 }}>
                <Btn
                  small
                  variant={p.popular ? "primary" : "default"}
                  to={`/signup?plan=${p.plan}`}
                  fullWidth
                >
                  {p.cta}
                </Btn>
                <div
                  style={{
                    fontSize: 11.5,
                    color: palette.subDim,
                    marginTop: 10,
                    textAlign: "center",
                  }}
                >
                  {p.note}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 22,
            marginTop: 28,
            fontSize: 12.5,
            color: palette.sub,
            flexWrap: "wrap",
          }}
        >
          {PM_CONTENT.trust.map((t) => (
            <span key={t}>· {t}</span>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ padding: "40px 32px 72px" }}>
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            borderRadius: 32,
            background: palette.panel,
            border: `1px solid ${palette.borderStrong}`,
            padding: "80px 64px",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: -200,
              left: "50%",
              transform: "translateX(-50%)",
              width: 800,
              height: 400,
              background: `radial-gradient(ellipse, ${palette.accent}22, transparent 60%)`,
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "relative" }}>
            <h2
              className="pm-section-h2"
              style={{
                fontSize: 64,
                fontWeight: 500,
                letterSpacing: "-0.04em",
                margin: 0,
                lineHeight: 1,
                textWrap: "balance",
              }}
            >
              Take control.
              <br />
              <span
                className="pm-serif"
                style={{ fontStyle: "italic", fontWeight: 400, color: palette.accent }}
              >
                Start today.
              </span>
            </h2>
            <p
              style={{
                fontSize: 18,
                color: palette.sub,
                maxWidth: 520,
                margin: "20px auto 0",
              }}
            >
              Replace the patchwork of tools with one AI-powered platform.
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 32, flexWrap: "wrap" }}>
              <Btn variant="primary" to="/signup">
                Start free trial<span aria-hidden>→</span>
              </Btn>
              <Btn href={`mailto:${PM_CONTENT.cta.contact}`}>Talk to sales</Btn>
            </div>
            <div style={{ fontSize: 13, color: palette.subDim, marginTop: 18 }}>
              {PM_CONTENT.cta.contact} &middot;{" "}
              <a href={WHATSAPP_HREF} target="_blank" rel="noreferrer" className="pm-link">
                WhatsApp {WHATSAPP_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        style={{
          padding: "32px 32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          maxWidth: 1280,
          margin: "0 auto",
          color: palette.sub,
          fontSize: 13,
          borderTop: `1px solid ${palette.border}`,
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src="/logo.png"
            alt="prayingmantis.cloud"
            style={{ height: 28, display: "block", opacity: 0.85 }}
          />
          <span>© {new Date().getFullYear()}</span>
        </div>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
          <Link to="/docs/guides/account-security" className="pm-link">
            Security
          </Link>
          <Link to="/privacy-policy" className="pm-link">
            Privacy
          </Link>
          <Link to="/terms-of-service" className="pm-link">
            Terms
          </Link>
          <Link to="/data-processing-addendum" className="pm-link">
            DPA
          </Link>
          <Link to="/refund-cancellation-policy" className="pm-link">
            Refunds &amp; Cancellations
          </Link>
          <Link to="/docs" className="pm-link">
            Docs
          </Link>
          <a href={`mailto:${PM_CONTENT.cta.contact}`} className="pm-link">
            Contact
          </a>
          <a href={WHATSAPP_HREF} target="_blank" rel="noreferrer" className="pm-link">
            WhatsApp
          </a>
        </div>
      </footer>
    </div>
  );
}
